import React, { useState, useEffect, createContext, useMemo, useCallback } from "react";
import Overview from "./Tabs/Overview";
import Archive from "./Tabs/Archive";
import TaxonomyString from "../../Components/Tooltips/TaxonomyString/TaxonomyString";
import Tabs, { TabPanel, TAB_DISPLAY } from "@acadia-ui/tabs";
import { helpers } from "@acadia-ui/theme";
import { ToastContainer, ToastNotification, showToast, TOAST_TYPE } from "@acadia-ui/toast";
import BookFiltersStore from "../../Flux/Stores/BookFiltersStore";
import MasterCampaignsStore from "../../Flux/Stores/MasterCampaignsStore";
import { onPostPush } from "./utils";
import AppSDK from "@growthos/ui-app-sdk";
import MediaMath from "../../Helpers/MediaMathHelpers";
import LoadingSpinner from "../../Components/LoadingSpinner";
import CampaignDownloadSidesheet from "./CampaignDownloadSidesheet/CampaignDownloadSidesheet";

import { fetchCampaignsByPage, fetchConfigAndPlatforms, getDataKeyDataValueFilters, getPermissionedDataKeys } from "../../Flux/Actions/MasterCampaignActions";

import "./index.scss";
import { useLocation, useNavigate } from "react-router-dom";
import { NavLink } from "@acadia-ui/navigation";

export const ViewMasterCampaignsContext = createContext();

const tabs = [
	{ label: "Overview", panelId: "overview-tab", disabled: false },
	{ label: "Archive", panelId: "archive-tab", disabled: false }
];

export const rowOptions = [
	{ label: "10", value: 10 },
	{ label: "20", value: 20 },
	{ label: "30", value: 30 },
	{ label: "40", value: 40 },
	{ label: "50", value: 50 }
];

export const ARCHIVE_REFRESH_REQUESTED_ORIGINATOR = {
	OVERVIEW_TAB: "overview_tab",
	ARCHIVE_TAB: "archive_tab",
	NONE: "none"
};

export default () => {
	const history = useNavigate();
	// raw data from the api
	const [data, setData] = useState(null);
	// filter options from the api
	const [dataKeyDataValueFilters, setDataKeyDataValueFilters] = useState();
	// available platforms from the api
	const [platforms, setPlatforms] = useState();
	// grid config, used by some actions like new primary campaign
	const [gridConfig, setGridConfig] = useState();

	// advertisers, not used at this level, maybe need to readd in the future.
	// const [advertisers, setAdvertisers] = useState({});

	// enabled platform APIs. used by actions like push to prisma, lock budget, etc
	const [enabledPlatformAPIs, setEnabledPlatformAPIs] = useState();
	// simply tracks the currently selected tab (overview or archive)
	const [selectedTab, setSelectedTab] = useState(0);
	// loading state will be informed by various api calls
	const [loading, setLoading] = useState(true);
	// cloned campaign data used for searching and highlighting
	const [clonedCampaignData, setClonedCampaignData] = useState();

	// permissioned data keys used for various permission/user group types in master campaign modal
	const [permissionedDataKeys, setPermissionedDataKeys] = useState({});

	const [isUSCurrency, setIsUSCurrency] = useState(false);

	const { pathname } = useLocation();

	useEffect(() => {
		MediaMath.determineIfUSCurrency([AppSDK.getContext()], () => {
			setIsUSCurrency(true);
		});
	}, []);

	// state to track how big the user wants pages to be (shared between Overview and Archive tabs)

	// hits the api when called
	const requestData = ({ onSuccess, onFailure, ...requestProps } = {}) => {
		setLoading(true);

		// fetch the data.
		// TODO remove this: we're moving data fetchign to the tab level
		return (
			fetchCampaignsByPage(requestProps)
				// .then(({ data: resData, totalCount }) => {
				// 	let out = [];
				// 	if (data === null || resetData) {
				// 		out = new Array(totalCount).fill({
				// 			MasterCampaignID: null,
				// 			ArchivedCampaigns: {},
				// 			ActivationCampaigns: {}
				// 		});
				// 	} else {
				// 		out = [...data];
				// 	}
				// 	out.splice((page - 1) * requestProps.rowsPerPage, resData.length, ...resData);
				// 	// setData(out);
				// 	return out;
				// })
				.then((d) => {
					onSuccess?.(d.data);
					return d;
				})
				.catch(async (error) => {
					try {
						const errorObject = await error.details.json();
						errorObject?.Errors?.forEach((errorMessage) => {
							showToast("masterCampaignToasts", <ToastNotification type={TOAST_TYPE.ERROR} message={errorMessage} />);
						});
					} catch (e) {
						console.log(e);
					}
					onFailure?.();
					return error;
				})
		);
	};

	const updateToastContainer = (banner) => {
		showToast("masterCampaignToasts", banner);
	};

	// component did mount
	useEffect(() => {
		if (pathname !== "/") {
			return;
		}
		getPermissionedDataKeys().then((response) => {
			const dataKeys = {};
			response.forEach((dataKey) => {
				if (!dataKeys[dataKey.DataKeyName]) {
					dataKeys[dataKey.DataKeyName] = [{ ...dataKey, label: dataKey.DataValueDescription, value: dataKey.DataValueID }];
				} else {
					dataKeys[dataKey.DataKeyName] = [...dataKeys[dataKey.DataKeyName], { ...dataKey, label: dataKey.DataValueDescription, value: dataKey.DataValueID }];
				}
			});
			setPermissionedDataKeys(dataKeys);
		});
		/* --------------------------------------------------------------------- */
		/* BEGIN EVENT LISTENER DEFINITIONS */
		/* --------------------------------------------------------------------- */
		const filtersFetched = () => {
			setDataKeyDataValueFilters(MasterCampaignsStore.getDataKeyDataValueFilters());
		};

		const refreshConfigAndPlatforms = () => {
			// setData(MasterCampaignsStore.getData());
			setPlatforms(MasterCampaignsStore.getMediaChannelsPlatforms());
			setGridConfig(MasterCampaignsStore.getGridConfig());
		};

		// const advertiserTaxonomyFetched = () => {
		// 	setAdvertisers(MasterCampaignsStore.getAdvertiserFields());
		// };

		const resetClonedActivationCampaign = () => {
			MasterCampaignsStore.resetClonedActivationCampaign();
		};

		const setPlatformAPIs = () => {
			const statues = BookFiltersStore.getSyncStatuses();
			setEnabledPlatformAPIs(statues);
		};

		const onPlatformPush = (platformName) => {
			setLoading(true);
			setTimeout(() => {
				requestData({
					onSuccess: (data) => {
						onPostPush(platformName, data);
					}
				});
			}, 9000);
		};

		const copyCampaignSuccess = () => {
			setClonedCampaignData({
				targetMasterCampaignID: MasterCampaignsStore.getTargetMasterCampaignID(),
				clonedActivationCampaign: MasterCampaignsStore.getClonedActivationCampaign(),
				previousActivationCampaignIDs: MasterCampaignsStore.getPreviousActivationCampaignIDs()
			});
			// requestData({
			// 	onSuccess: () => {
			// 		setTimeout(() => {
			// 			highlightClonedCampaign(MasterCampaignsStore.getTargetMasterCampaignID());
			// 			MasterCampaignsStore.resetClonedActivationCampaign();
			// 		}, 3000);
			// 	}
			// });
		};

		const onUnarchiveSuccess = (type) => {
			requestData({
				onSuccess: () => {
					updateToastContainer(<ToastNotification type={TOAST_TYPE.SUCCESS} message={"Data has successfully been unarchived."} timed={5000} />);
				}
			});
		};

		const onArchiveSuccess = () => {
			requestData({
				onSuccess: () => {
					updateToastContainer(<ToastNotification type={TOAST_TYPE.SUCCESS} message={"Data has successfully been archived."} timed={5000} />);
				}
			});
		};

		const onPrismaPush = () => onPlatformPush("Prisma");
		const onDV360Push = () => onPlatformPush("DV360");
		const onGCMPush = () => onPlatformPush("GCM");

		/* --------------------------------------------------------------------- */
		/* REGISTER EVENT LISTENERS */
		/* --------------------------------------------------------------------- */
		MasterCampaignsStore.on("fetchConfigAndPlatforms", refreshConfigAndPlatforms);
		MasterCampaignsStore.on("error_edit", refreshConfigAndPlatforms);
		MasterCampaignsStore.on("success_post", refreshConfigAndPlatforms);
		MasterCampaignsStore.on("error_post", refreshConfigAndPlatforms);
		MasterCampaignsStore.on("dataKeyDataValue_filters_fetched", filtersFetched);
		// MasterCampaignsStore.on("advertisers_fetched", advertiserTaxonomyFetched);
		MasterCampaignsStore.on("success_post", resetClonedActivationCampaign);
		MasterCampaignsStore.on("pushToPrismaSuccess", onPrismaPush);
		MasterCampaignsStore.on("pushToPrismaFailure", onPrismaPush);
		MasterCampaignsStore.on("pushToDV360Success", onDV360Push);
		MasterCampaignsStore.on("pushToDV360Failure", onDV360Push);
		MasterCampaignsStore.on("campaignPushedToGCM", onGCMPush);
		MasterCampaignsStore.on("archiveSuccess", onArchiveSuccess);
		MasterCampaignsStore.on("unarchiveSuccess", onUnarchiveSuccess);
		MasterCampaignsStore.on("copyCampaignSuccess", copyCampaignSuccess);

		BookFiltersStore.on("fetchSyncStatusesSuccess", setPlatformAPIs);
		BookFiltersStore.on("fetchSyncStatusesFailure", setPlatformAPIs);

		/* --------------------------------------------------------------------- */
		/* END EVENT LISTENER DEFINITIONS */
		/* --------------------------------------------------------------------- */

		// actual tasks
		document.title = "Placement Manager | Primary Campaigns Dashboard";
		TaxonomyString.loadTaxonomyString("activation_taxonomy_string");

		setPlatformAPIs();

		// fetch the platforms and config, then the dataKeyValueFilters
		fetchConfigAndPlatforms().then((d) => {
			// fetch the filter options for search filter data key/values
			const filterChoices = BookFiltersStore.getChoicesState();
			if (Object.values(filterChoices).every((val) => val != null)) {
				getDataKeyDataValueFilters("Campaign");
			}
			return d;
		});

		// return a destructor that cleans up the listeners
		return () => {
			MasterCampaignsStore.removeListener("fetchConfigAndPlatforms", refreshConfigAndPlatforms);
			MasterCampaignsStore.removeListener("error_edit", refreshConfigAndPlatforms);
			MasterCampaignsStore.removeListener("success_post", refreshConfigAndPlatforms);
			MasterCampaignsStore.removeListener("error_post", refreshConfigAndPlatforms);
			MasterCampaignsStore.removeListener("dataKeyDataValue_filters_fetched", filtersFetched);
			// MasterCampaignsStore.removeListener("advertisers_fetched", advertiserTaxonomyFetched);
			MasterCampaignsStore.removeListener("success_post", resetClonedActivationCampaign);
			MasterCampaignsStore.removeListener("pushToPrismaSuccess", onPrismaPush);
			MasterCampaignsStore.removeListener("pushToPrismaFailure", onPrismaPush);
			MasterCampaignsStore.removeListener("pushToDV360Success", onDV360Push);
			MasterCampaignsStore.removeListener("pushToDV360Failure", onDV360Push);
			MasterCampaignsStore.removeListener("campaignPushedToGCM", onGCMPush);
			MasterCampaignsStore.removeListener("archiveSuccess", onArchiveSuccess);
			MasterCampaignsStore.removeListener("unarchiveSuccess", onUnarchiveSuccess);
			MasterCampaignsStore.removeListener("copyCampaignSuccess", copyCampaignSuccess);

			BookFiltersStore.removeListener("fetchSyncStatusesSuccess", setPlatformAPIs);
			BookFiltersStore.removeListener("fetchSyncStatusesFailure", setPlatformAPIs);
		};
	}, [pathname]);

	// //use various pieces of state to change the loading state
	useEffect(() => {
		if (data && platforms && dataKeyDataValueFilters && gridConfig && enabledPlatformAPIs) {
			setLoading(false);
			// Reset the checkbox state when data is updated
			// setTimeout(() => {
			// 	const checkboxes = document.querySelectorAll(".gos-dt tbody .gos-chk input");
			// 	if (checkboxes) {
			// 		Array.from(checkboxes).forEach((checkbox) => {
			// 			checkbox.checked = false;
			// 			checkbox.indeterminate = false;
			// 		});
			// 	}
			// }, 1000);
		} else {
			setLoading(true);
		}
	}, [data, platforms, dataKeyDataValueFilters, gridConfig, enabledPlatformAPIs]);

	const [actions, setActions] = useState();
	const [isCrossPlatform, setIsCrossPlatform] = useState(false);
	const [archiveRefreshRequested, setArchiveRefreshRequested] = useState(ARCHIVE_REFRESH_REQUESTED_ORIGINATOR.NONE);

	// prepare the context with all of the state
	const contextValue = useMemo(() => {
		return {
			data,
			platforms,
			dataKeyDataValueFilters,
			gridConfig,
			// advertisers,
			enabledPlatformAPIs,
			refreshData: requestData,
			loading,
			clonedCampaignData,
			selectedTab,
			addBanner: updateToastContainer,
			setActions,
			isCrossPlatform,
			setIsCrossPlatform,
			permissionedDataKeys,
			archiveRefreshRequested,
			setArchiveRefreshRequested,
			isUSCurrency
		};
	}, [
		data,
		platforms,
		dataKeyDataValueFilters,
		gridConfig,
		// advertisers,
		enabledPlatformAPIs,
		requestData,
		loading,
		clonedCampaignData,
		selectedTab,
		updateToastContainer,
		setActions,
		isCrossPlatform,
		setIsCrossPlatform,
		permissionedDataKeys,
		archiveRefreshRequested,
		setArchiveRefreshRequested,
		isUSCurrency
	]);

	const subNavLinks = useMemo(
		() => [
			<NavLink
				// this comment exists to prevent eslint from making this unreadable.
				key="overview"
				href=""
				selected={selectedTab == 0}
				onClick={() => setSelectedTab(0)}
			>
				Overview
			</NavLink>,
			<NavLink
				// this comment exists to prevent eslint from making this unreadable.
				key="archive"
				href=""
				selected={selectedTab == 1}
				onClick={() => setSelectedTab(1)}
			>
				Archive
			</NavLink>
		],
		[selectedTab]
	);

	const element = useMemo(
		() => (
			<div className="view-master-campaigns-container">
				<ViewMasterCampaignsContext.Provider value={contextValue}>
					<Tabs tabs={tabs} selectedTab={selectedTab} onSelect={setSelectedTab} display={TAB_DISPLAY.BUTTON}>
						<TabPanel id="overview-tab" role="tabpanel" aria-labelledby="Overview">
							<Overview />
						</TabPanel>
						<TabPanel id="archive-tab" role="tabpanel" aria-labelledby="Archive">
							<Archive />
						</TabPanel>
					</Tabs>
					<ToastContainer id="masterCampaignToasts" />
				</ViewMasterCampaignsContext.Provider>
				<CampaignDownloadSidesheet />
			</div>
		),
		[contextValue, loading, selectedTab]
	);

	return useMemo(
		() => ({
			subNavLinks,
			actions,
			element
		}),
		[subNavLinks, actions, element]
	);
};
